// «Календарь Google»: подключение, календарь для сессий, направление, личные дела, синхронизация сейчас;
// «Перенос из календаря»: что нашлось (имена, коды) → отметить клиентов → перенести. Вид — как в первой версии.
import { addDays, isCommercial, plural, ruDateShort } from '@praktika/core';
import { afterChange, call, rpc, saveSettings } from '../lib/api.ts';
import { resetBusy } from '../lib/busy.ts';
import { $, $$, busy, esc, toast } from '../lib/dom.ts';
import { defaultGroup, groupById } from '../lib/data.ts';
import { googleAuth } from '../lib/login.ts';
import { SCREENS, go, render, setTab, title } from '../lib/nav.ts';
import { chooseSheet, kvBtn, kvTog, promptSheet } from '../lib/sheet.ts';
import { S } from '../lib/state.ts';
import { nowInfo } from '../lib/zones.ts';
import { bindCommon } from './common.ts';

const SYNC_MODES: Array<[string, string]> = [['both', 'в обе стороны'], ['push', 'только приложение → календарь'],
  ['pull', 'только календарь → приложение']];
const pairLabel = (list: Array<[string, string]>, v: string) => (list.find((x) => x[0] === v) ?? list[0]!)[1];

interface CalItem { id: string; name: string; primary: boolean; owned: boolean; current: boolean }
const NEW_CALENDAR = '__new';

async function calendars(): Promise<CalItem[] | null> {
  busy(true);
  try { return await rpc<CalItem[]>('calendars'); } catch (e) { toast((e as Error).message, true); return null; }
  finally { busy(false); }
}

// промежуток «синхронизировать сейчас» — пока открыт экран
let range: { from: string; to: string } | null = null, autoPicked = false;

SCREENS.calendar = (app) => {
  const s = S.settings, c = S.calendar, n = nowInfo().date;
  const intro = '<div class="p-sum">Сессии пишутся в выбранный календарь Google, а изменения в нём (перенос, удаление, ' +
    'новое событие с именем клиента) возвращаются в приложение. Проверка каждые 15 минут и при открытии приложения.</div>';
  if (!c.available || !c.access) {
    app.innerHTML = title('Календарь Google') + intro + (c.available ? '<div class="btnrow"><button class="btn pri" ' +
      'data-cal-connect>Подключить календарь Google</button></div><p class="p-note">Google спросит разрешение ' +
      'на календарь: приложение будет записывать туда сессии и видеть изменения. Google может предупредить, что ' +
      'приложение не проверено, — это нормально для своего приложения.</p>' :
      '<div class="empty">Календарь подключается после входа через Google.</div>');
    const connect = $('[data-cal-connect]', app);
    if (connect) connect.onclick = () => googleAuth('calendar');
    return;
  }
  // кабинет перенесён из первой версии: сессии привязаны к встречам её календаря — он и выбирается сам
  if (!c.connected && s.cal_source && !autoPicked) {
    autoPicked = true;
    void call<{ name: string }>('setCalendar', s.cal_source).then(() => afterChange('Календарь для сессий — тот же, что ' +
      'в первой версии'), () => {});
  }
  if (!range) range = { from: addDays(n, -(Number(s.sync_back) || 7)), to: addDays(n, Number(s.sync_ahead) || 120) };
  const r = range, bc = c.busyCals;
  app.innerHTML = title('Календарь Google') + intro +
    (c.error ? '<div class="p-alert soft"><div class="h">Календарь не ответил</div>' + esc(c.error) +
      '<button data-reauth>Подключить заново</button></div>' : '') +
    (c.connected && s.cal_source && c.calendarId !== s.cal_source ? '<div class="p-alert soft"><div class="h">Сессии ' +
      'перенесены из другого календаря</div>Их встречи в выбранном календаре не найдутся: приложение привяжет сессии к ' +
      'встречам этого календаря в то же время, остальное покажет в «Не узнаны».<button data-src>Вернуть тот ' +
      'календарь</button></div>' : '') +
    '<div class="card">' + kvBtn('Календарь для сессий', c.connected ? c.calendarName || 'подключён' : 'выберите', 'data-pick') +
      kvBtn('Направление', pairLabel(SYNC_MODES, s.sync_mode), 'data-mode') + kvBtn('Не узнаны',
      S.counts.unknown ? S.counts.unknown + ' ' + plural(S.counts.unknown, 'встреча', 'встречи', 'встреч') + ' — разобрать' :
        'все встречи узнаны', 'data-go="unknown"') + kvBtn('Автоматически смотреть', (Number(s.sync_back) || 7) +
      ' дн. назад, ' + (Number(s.sync_ahead) || 120) + ' вперёд', 'data-range') + '</div>' +
    '<div class="btnrow"><button class="btn sec" data-import-cal>Перенести встречи из календаря…</button></div>' +
    '<div class="card"><div class="hd">Личные дела в расписании</div>' + (bc.length ? bc.map((x, i) =>
      kvBtn(x.name || x.id, x.mode === 'busy' ? 'только «занято»' : 'с названием', 'data-bc="' + i + '"')).join('') :
      '<p class="p-note" style="padding:4px 14px 8px">Подключите свой обычный календарь: его события будут видны в ' +
      'расписании серым, их время не предлагается для записи, а событие на весь день помечает день как занятый.</p>') +
      '<button class="kv act" data-bc-add><span class="k">+ Добавить календарь</span><span class="v chev"></span></button></div>' +
    '<div class="card"><div class="hd">Синхронизировать сейчас</div><div style="display:flex;gap:8px">' +
      '<div class="field" style="flex:1;margin:0 0 8px"><div class="l">С</div><input type="date" data-from value="' +
      esc(r.from) + '"></div><div class="field" style="flex:1;margin:0 0 8px"><div class="l">По</div>' +
      '<input type="date" data-to value="' + esc(r.to) + '"></div></div>' +
      '<div class="btnrow" style="padding:0"><button class="btn pri" data-sync' + (c.connected ? '' : ' disabled') +
      '>Синхронизировать</button></div><p class="p-note">Последняя синхронизация: ' +
      esc(c.lastSync || 'ещё не было') + '</p></div>' +
    (c.connected ? '<div class="btnrow" style="margin-top:12px"><button class="btn txt" data-off>Отключить календарь</button></div>' : '');
  bindCommon(app);
  const reauth = $('[data-reauth]', app);
  if (reauth) reauth.onclick = () => googleAuth('calendar');
  const back = $('[data-src]', app);
  if (back) back.onclick = () => { void call('setCalendar', s.cal_source).then(() => afterChange('Календарь вернули'), () => {}); };
  $('[data-import-cal]', app).onclick = () => { imp = null; go('calImport'); };
  const from = $<HTMLInputElement>('[data-from]', app), to = $<HTMLInputElement>('[data-to]', app);
  from.onchange = () => { r.from = from.value; };
  to.onchange = () => { r.to = to.value; };
  $('[data-pick]', app).onclick = async () => {
    const list = await calendars();
    if (!list) return;
    chooseSheet('Календарь для сессий', list.filter((x) => x.owned).map((x) => ({ value: x.id,
      label: x.name + (x.primary ? ' (основной)' : '') })).concat([{ value: NEW_CALENDAR, label: 'Новый календарь «Практика»' }]),
    c.calendarId, (id) => {
      if (id === c.calendarId) return;
      if (c.connected && !confirm('Переключить календарь? События сессий переедут в него при следующей синхронизации.')) return;
      void call<{ name: string }>('setCalendar', id).then((x) => afterChange('Календарь: ' + (x.name || '')), () => {});
    });
  };
  $('[data-mode]', app).onclick = () => chooseSheet('Направление', SYNC_MODES.map((x) => ({ value: x[0], label: x[1] })),
    s.sync_mode || 'both', (v) => void saveSettings({ sync_mode: v }));
  $('[data-range]', app).onclick = () => promptSheet('Дней назад', s.sync_back || 7, 'number', (v1) =>
    promptSheet('Дней вперёд', s.sync_ahead || 120, 'number', (v2) => void saveSettings({ sync_back: Number(v1) || 7,
      sync_ahead: Number(v2) || 120 })));
  $('[data-sync]', app).onclick = () => void call<string>('syncCalendar', { from: r.from, to: r.to })
    .then((m) => afterChange(m || 'Синхронизировано'), () => {});
  const saveBc = (list: typeof bc, msg: string) => void call('setBusyCals', list).then(() => { resetBusy(); return afterChange(msg); },
    () => {});
  $$('[data-bc]', app).forEach((b) => {
    b.onclick = () => {
      const i = Number(b.getAttribute('data-bc')), x = bc[i];
      if (!x) return;
      chooseSheet('Как показывать «' + (x.name || '') + '»', [{ value: 'full', label: 'С названием и временем',
        hint: 'например «Стоматолог 15:00»' }, { value: 'busy', label: 'Только «занято»', hint: 'без названий' },
      { value: '__del', label: 'Убрать из расписания' }], x.mode === 'busy' ? 'busy' : 'full', (v) => {
        const list = bc.slice();
        if (v === '__del') list.splice(i, 1);
        else list[i] = { id: x.id, name: x.name, mode: v === 'busy' ? 'busy' : 'full' };
        saveBc(list, v === '__del' ? 'Календарь убран' : 'Сохранено');
      });
    };
  });
  $('[data-bc-add]', app).onclick = async () => {
    const list = await calendars();
    if (!list) return;
    const have = new Set(bc.map((x) => x.id)), opts = list.filter((x) => !have.has(x.id) && x.id !== c.calendarId)
      .map((x) => ({ value: x.id, label: x.name + (x.primary ? ' (основной)' : ''), hint: x.owned ? '' : 'подписка' }));
    if (!opts.length) { toast('Все календари уже подключены'); return; }
    chooseSheet('Какой календарь показывать', opts, '', (id) => {
      const x = list.find((y) => y.id === id);
      saveBc(bc.concat([{ id, name: x ? x.name : id, mode: 'full' }]), 'Календарь добавлен');
    });
  };
  const off = $('[data-off]', app);
  if (off) off.onclick = () => {
    if (!confirm('Отключить календарь Google? Встречи в календаре останутся, приложение перестанет их видеть и менять.')) return;
    void call('disconnectCalendar').then(() => { range = null; resetBusy(); return afterChange('Календарь отключён'); }, () => {});
  };
};

// ---------------------------------------------------------------- перенос уже назначенных встреч из календаря
interface Preview {
  calendarId: string; from: string; to: string; total: number; linked: number;
  groups: Array<{ key: string; name: string; n: number; first: string; last: string; codes: string[]; numFirst: number;
    numLast: number; messenger: string; existing: boolean; pick: boolean }>;
  codes: Array<{ code: string; n: number }>;
}
interface ImportDraft {
  step: 1 | 2; calId: string; calName: string; from: string; to: string; data: Preview | null;
  picks: Record<string, boolean>; termsByCode: Record<string, string>; pastDone: boolean; oneWay: boolean;
}
let imp: ImportDraft | null = null;

SCREENS.calImport = (app) => {
  const n = nowInfo().date;
  if (!imp) imp = { step: 1, calId: S.calendar.calendarId, calName: S.calendar.calendarName, from: addDays(n, -30),
    to: addDays(n, 120), data: null, picks: {}, termsByCode: {}, pastDone: true, oneWay: true };
  const c = imp;
  if (c.step === 1 || !c.data) {
    app.innerHTML = title('Перенос из календаря') +
      '<div class="p-sum">Приложение прочитает встречи из выбранного календаря и покажет список имён. Вы отметите ' +
      'клиентов, и все их встречи появятся в приложении. Сами события в календаре не меняются.</div>' +
      '<div class="card">' + kvBtn('Календарь', c.calName || 'выберите', 'data-pick') + '</div>' +
      '<div class="card"><div style="display:flex;gap:8px"><div class="field" style="flex:1;margin:0 0 8px">' +
      '<div class="l">С</div><input type="date" data-cfrom value="' + esc(c.from) + '"></div><div class="field" ' +
      'style="flex:1;margin:0 0 8px"><div class="l">По</div><input type="date" data-cto value="' + esc(c.to) +
      '"></div></div></div><div class="btnrow"><button class="btn pri" data-find' + (c.calId ? '' : ' disabled') +
      '>Показать, что найдено</button></div>';
    bindCommon(app);
    const f = $<HTMLInputElement>('[data-cfrom]', app), t = $<HTMLInputElement>('[data-cto]', app);
    f.onchange = () => { c.from = f.value; };
    t.onchange = () => { c.to = t.value; };
    $('[data-pick]', app).onclick = async () => {
      const list = await calendars();
      if (!list) return;
      chooseSheet('Календарь', list.map((x) => ({ value: x.id, label: x.name + (x.primary ? ' (основной)' : '') })), c.calId,
        (id) => { const x = list.find((y) => y.id === id); c.calId = id; c.calName = x ? x.name : ''; render(); });
    };
    $('[data-find]', app).onclick = () => void call<Preview>('calendarPreview', { calendarId: c.calId, from: c.from, to: c.to })
      .then((d) => {
        c.data = d;
        c.picks = {};
        for (const g of d.groups) if (g.pick) c.picks[g.key] = true;
        c.termsByCode = {};
        c.step = 2;
        render();
        window.scrollTo(0, 0);
      }, () => {});
    return;
  }
  const d = c.data;
  let nCl = 0, nSe = 0;
  for (const g of d.groups) if (c.picks[g.key]) { nCl++; nSe += g.n; }
  const groupName = (v: string) => groupById(v)?.name ?? v;
  let html = title('Что нашлось', d.total + ' ' + plural(d.total, 'встреча', 'встречи', 'встреч')) +
    '<div class="p-sum">' + esc(ruDateShort(d.from)) + ' – ' + esc(ruDateShort(d.to)) + '. Отметьте клиентов. Остальное ' +
    '(личные дела, поездки, учёба) останется только в календаре.' + (d.linked ? ' Уже перенесено раньше: ' + d.linked + '.' :
    '') + '</div>';
  if (d.codes.length) html += '<div class="card"><div class="hd">Коды в названиях: чьи это клиенты</div>' + d.codes.map((x) =>
    kvBtn('«' + x.code + '» · ' + x.n + ' ' + plural(x.n, 'встреча', 'встречи', 'встреч'), 'группа «' +
      (c.termsByCode[x.code] ? groupName(c.termsByCode[x.code]!) : defaultGroup().name) + '»', 'data-code="' + esc(x.code) + '"'))
    .join('') + '</div>';
  html += '<div class="card">' + kvTog('Прошедшие встречи считать состоявшимися', c.pastDone, 'data-past') +
    kvTog('Только читать календарь: приложение ничего в него не пишет', c.oneWay, 'data-oneway') + '</div>';
  html += '<div class="card"><div class="hd">Имена · отмечено ' + nCl + ' из ' + d.groups.length + '</div>' +
    d.groups.map((g, i) => '<div class="kv"><div style="min-width:0"><div class="v" style="text-align:left">' + esc(g.name) +
      (g.existing ? ' <span class="small">· уже клиент</span>' : '') + '</div><div class="k" style="font-size:12px">' + g.n +
      ' ' + plural(g.n, 'встреча', 'встречи', 'встреч') + ' · ' + esc(ruDateShort(g.first)) + (g.last !== g.first ? ' – ' +
      esc(ruDateShort(g.last)) : '') + (g.codes.length ? ' · ' + esc(g.codes.join(', ')) : '') + (g.numLast ? ' · №' +
      g.numFirst + (g.numLast !== g.numFirst ? '–' + g.numLast : '') : '') + (g.messenger ? ' · ' + esc(g.messenger) : '') +
      '</div></div><button class="tog' + (c.picks[g.key] ? ' on' : '') + '" data-g="' + i + '"></button></div>').join('') +
    '</div><div class="btnrow"><button class="btn pri" data-run' + (nSe ? '' : ' disabled') + '>Перенести: ' + nCl + ' ' +
    plural(nCl, 'клиент', 'клиента', 'клиентов') + ', ' + nSe + ' ' + plural(nSe, 'встреча', 'встречи', 'встреч') +
    '</button></div><div class="btnrow"><button class="btn sec" data-all>Отметить все</button>' +
    '<button class="btn sec" data-none>Снять все</button></div>';
  app.innerHTML = html;
  bindCommon(app);
  $$('[data-g]', app).forEach((b) => {
    b.onclick = () => {
      const g = d.groups[Number(b.getAttribute('data-g'))]!;
      if (c.picks[g.key]) delete c.picks[g.key]; else c.picks[g.key] = true;
      const y = window.scrollY;
      render();
      window.scrollTo(0, y);
    };
  });
  $$('[data-code]', app).forEach((b) => {
    b.onclick = () => {
      const code = b.getAttribute('data-code')!;
      const opts = S.groups.filter((g) => !g.isDefault).map((g) => ({ value: g.id, label: 'группа «' + g.name + '»',
        hint: isCommercial(g) ? 'проект' : 'личная' }));
      for (const v of Object.values(c.termsByCode)) {
        if (v && !groupById(v) && !opts.some((o) => o.value === v)) opts.push({ value: v, label: 'группа «' + v + '»', hint: 'новая' });
      }
      chooseSheet('Клиенты с кодом «' + code + '»', [{ value: '', label: 'группа «' + defaultGroup().name + '»', hint: '' },
        ...opts, { value: '+', label: '+ новая группа…', hint: '' }], c.termsByCode[code] || '', (v) => {
        if (v === '+') {
          promptSheet('Название группы', 'Корпоративные клиенты', 'text', (nm) => {
            const t = String(nm || '').trim();
            if (t) { c.termsByCode[code] = t; render(); }
          });
          return;
        }
        if (v) c.termsByCode[code] = v; else delete c.termsByCode[code];
        render();
      });
    };
  });
  $('[data-past]', app).onclick = () => { c.pastDone = !c.pastDone; render(); };
  $('[data-oneway]', app).onclick = () => { c.oneWay = !c.oneWay; render(); };
  $('[data-all]', app).onclick = () => { for (const g of d.groups) c.picks[g.key] = true; render(); };
  $('[data-none]', app).onclick = () => { c.picks = {}; render(); };
  $('[data-run]', app).onclick = () => void call<{ message: string }>('calendarImport', { calendarId: d.calendarId, from: d.from,
    to: d.to, keys: Object.keys(c.picks), termsByCode: c.termsByCode, pastDone: c.pastDone, oneWay: c.oneWay }).then((r) => {
    imp = null;
    range = null;
    return afterChange(r.message || 'Перенесено').then(() => setTab('clients'));
  }, () => {});
};
